/**
 * Deterministic randomness.
 *
 * Every "unique" decision the layout engine makes is driven from here, seeded
 * with the menu instance id. A guest scanning the QR, the tablet in the dining
 * room and the preview in the constructor must all resolve to byte-identical
 * pages, so `Math.random` is never acceptable in the pipeline.
 */
/** FNV-1a, 32-bit. Stable across runtimes, which `String.prototype.hashCode`-style ad hoc hashes are not. */
export function hashString(input) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i += 1) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}
/**
 * mulberry32. Small, fast, and good enough for layout jitter; the important
 * property here is reproducibility, not cryptographic quality.
 */
export function createPrng(seed) {
    const initial = typeof seed === 'number' ? seed >>> 0 : hashString(seed);
    let state = initial === 0 ? 0x9e3779b9 : initial;
    const next = () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let t = state;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const prng = {
        next,
        int(min, max) {
            if (max <= min)
                return min;
            return min + Math.floor(next() * (max - min + 1));
        },
        pick(items) {
            if (items.length === 0)
                return undefined;
            return items[Math.floor(next() * items.length)];
        },
        weighted(items, weight) {
            if (items.length === 0)
                return undefined;
            const weights = items.map((item) => Math.max(0, weight(item)));
            const total = weights.reduce((sum, w) => sum + w, 0);
            if (total <= 0)
                return prng.pick(items);
            let threshold = next() * total;
            for (let i = 0; i < items.length; i += 1) {
                threshold -= weights[i] ?? 0;
                if (threshold <= 0)
                    return items[i];
            }
            return items[items.length - 1];
        },
        shuffle(items) {
            const copy = [...items];
            for (let i = copy.length - 1; i > 0; i -= 1) {
                const j = Math.floor(next() * (i + 1));
                const swap = copy[i];
                copy[i] = copy[j];
                copy[j] = swap;
            }
            return copy;
        },
        chance(probability) {
            return next() < probability;
        },
        fork(label) {
            return createPrng(hashString(`${initial}:${label}`));
        },
    };
    return prng;
}
//# sourceMappingURL=prng.js.map